import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard, FlaskConical, BookOpen, ListChecks, FolderKanban,
  Trophy, Users, FileEdit, GraduationCap, LogOut, Menu, X, History,
} from "lucide-react";

const NAV = {
  student: [
    { to: "/dashboard", label: "Dasbor", icon: LayoutDashboard },
    { to: "/lab", label: "Lab Virtual", icon: FlaskConical },
    { to: "/history", label: "Riwayat", icon: History },
    { to: "/materials", label: "Materi", icon: BookOpen },
    { to: "/scenarios", label: "Skenario", icon: ListChecks },
    { to: "/pjbl", label: "PjBL", icon: FolderKanban },
    { to: "/leaderboard", label: "Papan Skor", icon: Trophy },
  ],
  lecturer: [
    { to: "/dashboard", label: "Dasbor", icon: LayoutDashboard },
    { to: "/lecturer/content", label: "Konten", icon: FileEdit },
    { to: "/lecturer/grading", label: "PjBL & Penilaian", icon: GraduationCap },
    { to: "/lab", label: "Lab Virtual", icon: FlaskConical },
    { to: "/scenarios", label: "Skenario", icon: ListChecks },
    { to: "/leaderboard", label: "Papan Skor", icon: Trophy },
  ],
  admin: [
    { to: "/dashboard", label: "Dasbor", icon: LayoutDashboard },
    { to: "/admin", label: "Pengguna & Data", icon: Users },
    { to: "/lecturer/content", label: "Konten", icon: FileEdit },
    { to: "/lab", label: "Lab Virtual", icon: FlaskConical },
    { to: "/leaderboard", label: "Papan Skor", icon: Trophy },
  ],
};

const ROLE_LABEL = { student: "Mahasiswa", lecturer: "Dosen", admin: "Admin" };

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const items = NAV[user.role] || NAV.student;

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const SidebarInner = (
    <div className="flex flex-col h-full">
      <div className="px-5 py-5 flex items-center gap-2.5 border-b border-border">
        <div className="h-9 w-9 rounded-md bg-primary flex items-center justify-center">
          <FlaskConical className="h-5 w-5 text-primary-foreground" />
        </div>
        <div className="leading-tight">
          <div className="font-heading font-extrabold text-[15px]">HPLC Lab</div>
          <div className="text-[11px] text-muted-foreground font-data">Virtual Chromatography</div>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            onClick={() => setOpen(false)}
            data-testid={`nav-${it.to.replace(/\//g, "-").replace(/^-/, "")}`}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors",
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              )
            }
          >
            <it.icon className="h-[18px] w-[18px]" />
            {it.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-border">
        <div className="px-2 py-2 mb-2">
          <div className="text-sm font-semibold truncate">{user.name}</div>
          <div className="text-[11px] text-muted-foreground font-data">
            {ROLE_LABEL[user.role]}{user.nim ? ` · ${user.nim}` : ""}
          </div>
        </div>
        <Button variant="outline" size="sm" className="w-full justify-start gap-2" onClick={handleLogout} data-testid="logout-button">
          <LogOut className="h-4 w-4" /> Keluar
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 border-r border-border bg-card flex-shrink-0 sticky top-0 h-screen">
        {SidebarInner}
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="relative w-64 bg-card border-r border-border">{SidebarInner}</aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="lg:hidden sticky top-0 z-40 flex items-center justify-between px-4 h-14 border-b border-border bg-card">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded bg-primary flex items-center justify-center">
              <FlaskConical className="h-4 w-4 text-primary-foreground" />
            </div>
            <span className="font-heading font-extrabold">HPLC Lab</span>
          </div>
          <Button variant="ghost" size="icon" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
